import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { setTenantId, setTenantBackendUrl } from './api/tenantForApi';

const TenantContext = createContext(null);

const TENANT_INFO_API_URL = process.env.REACT_APP_TENANT_INFO_API_URL || '';

// Fallback when the tenant has no logo of its own: the deployment's env logo if
// set, else the bundled default. One fallback chain for every logo in the app
// (tenant logo -> env logo -> bundled default). TODO: replace with per-tenant logo.
const DEFAULT_LOGO = process.env.REACT_APP_APP_LOGO
  ? `/${process.env.REACT_APP_APP_LOGO}`
  : '/appLogo.png';

/**
 * Strip port from host for API lookup (backend stores e.g. tenant1.localhost, not tenant1.localhost:3002)
 */
function hostWithoutPort(host) {
  if (!host || typeof host !== 'string') return host || '';
  const trimmed = host.trim();
  const idx = trimmed.lastIndexOf(':');
  if (idx <= 0) return trimmed;
  const after = trimmed.slice(idx + 1);
  if (/^\d+$/.test(after)) return trimmed.slice(0, idx);
  return trimmed;
}

const DEV_TENANT_OVERRIDE_KEY = 'tvid.web.devTenantDomain';

function isLocalDevHost(host) {
  return host === 'localhost' || host === '127.0.0.1';
}

// Local dev has no real hostname to look up (SuperAdmin only knows real
// domains like grieftv.tvidservices.com). `?tenant_domain=` on first load
// picks which tenant to resolve as, remembered in localStorage so plain
// http://localhost:3003/... keeps working after that.
function resolveHostForApi(rawHost) {
  const host = hostWithoutPort(rawHost);
  if (!isLocalDevHost(host)) return host;

  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('tenant_domain');
    if (fromQuery) {
      window.localStorage.setItem(DEV_TENANT_OVERRIDE_KEY, fromQuery);
      return fromQuery;
    }
    return window.localStorage.getItem(DEV_TENANT_OVERRIDE_KEY) || host;
  } catch {
    return host;
  }
}

/**
 * Fetch tenant info from Super Admin backend by current host.
 * GET /api/app/tenant-info with header X-Tenant-Domain: <host>
 */
async function fetchTenantByHost(apiBaseUrl, host) {
  const base = apiBaseUrl.replace(/\/$/, '');
  const url = `${base}/api/app/tenant-info`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Domain': host,
    },
    cache: 'no-store',
  });
  const data = await res.json().catch(() => ({ tenant: null }));
  return data;
}

// Remember the last resolved tenant per host so the very first paint (the
// full-screen <Loader/> shown while tenant-info is still being fetched) already
// has the tenant's logo/name instead of the platform default. Keyed by host so
// one tenant's cache never bleeds into another's. The network fetch still runs
// and reconciles on every load.
const TENANT_CACHE_PREFIX = 'tvid.tenant.';

function readCachedTenant(host) {
  try {
    if (!host || typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(TENANT_CACHE_PREFIX + host);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null; // privacy mode / bad JSON — fall back to default
  }
}

function writeCachedTenant(host, tenantObj) {
  try {
    if (!host || typeof window === 'undefined') return;
    if (tenantObj) {
      window.localStorage.setItem(TENANT_CACHE_PREFIX + host, JSON.stringify(tenantObj));
    } else {
      window.localStorage.removeItem(TENANT_CACHE_PREFIX + host);
    }
  } catch {
    /* ignore storage errors (quota / privacy mode) */
  }
}

export const TenantProvider = ({ children }) => {
  const initialHost =
    typeof window !== 'undefined' ? hostWithoutPort(window.location.hostname) : '';
  const [tenant, setTenant] = useState(() =>
    TENANT_INFO_API_URL ? readCachedTenant(initialHost) : null
  );
  const [loading, setLoading] = useState(!!TENANT_INFO_API_URL);
  const [error, setError] = useState(null);
  const [host, setHost] = useState(initialHost);

  useEffect(() => {
    if (!TENANT_INFO_API_URL) {
      setTenantId(null);
      setTenantBackendUrl(null);
      setLoading(false);
      return;
    }

    const rawHost = typeof window !== 'undefined' ? window.location.hostname : '';
    const hostForApi = resolveHostForApi(rawHost);
    setHost(hostForApi);

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchTenantByHost(TENANT_INFO_API_URL, hostForApi)
      .then((data) => {
        if (cancelled) return;
        if (data.tenant) {
          setTenant(data.tenant);
          setError(null);
          setTenantId(data.tenant.id);
          // The tenant's real backendUrl (e.g. https://api-grieftv.tvidservices.com/api)
          // is for production. Locally we still want the real tenant resolved
          // (for id/branding) but keep hitting the local backend for API calls.
          const runningLocally = isLocalDevHost(rawHost);
          setTenantBackendUrl(runningLocally ? null : (data.tenant.backendUrl ?? null));
          writeCachedTenant(hostForApi, data.tenant); // refresh cache for next load
        } else {
          setTenant(null);
          setError(data.message || 'No tenant found for this address.');
          setTenantId(null);
          setTenantBackendUrl(null);
          writeCachedTenant(hostForApi, null); // tenant genuinely gone — drop stale cache
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setTenant(null);
        setError(err?.message || 'Failed to resolve tenant.');
        setTenantId(null);
        setTenantBackendUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const logoUrl = tenant?.logoUrl || null;

  const value = useMemo(
    () => ({
      tenant,
      loading,
      error,
      host,
      /** Tenant logo URL if set */
      logoUrl,
      /** Logo to display: tenant logo if set, else default */
      logo: logoUrl || DEFAULT_LOGO,
      /** Whether multi-tenant resolution is enabled (env set) */
      isMultiTenant: !!TENANT_INFO_API_URL,
    }),
    [tenant, loading, error, host, logoUrl]
  );

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return ctx;
};

export default TenantContext;
